import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pencil, PowerOff, Power, Plus, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useServiceLines } from "../lib/serviceLines";
import BulkImportTeamMembersDialog from "./admin/BulkImportTeamMembersDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const SERVICE_LINES = ["GHA", "GHBC", "GLH", "TVA", "FH", "GHI"];

function TeamMemberDialog({ trigger, initial, onSaved, hrPartners }) {
  const isEdit = !!initial?.id;
  const { user } = useAuth();
  const canEditPositionId = !isEdit || Boolean(user?.is_admin);
  const { nameOf } = useServiceLines();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(() => ({
    position_id:
      initial?.position_id || (initial?.file_number != null ? String(initial.file_number) : ""),
    name: initial?.name || "",
    department: initial?.department || "",
    service_line: initial?.service_line || "GHA",
    assigned_hr_partner_id: initial?.assigned_hr_partner_id || "",
  }));
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({
        position_id:
          initial?.position_id || (initial?.file_number != null ? String(initial.file_number) : ""),
        name: initial?.name || "",
        department: initial?.department || "",
        service_line: initial?.service_line || "GHA",
        assigned_hr_partner_id: initial?.assigned_hr_partner_id || "",
      });
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = { ...form };
      const originalPid =
        initial?.position_id ||
        (initial?.file_number != null ? String(initial.file_number) : "");
      const normalisedPid = (form.position_id || "").trim().toUpperCase();
      if (!isEdit) {
        // Normalise to uppercase alphanumeric. Backend re-validates against
        // ^[A-Z0-9]{3,20}$ and rejects with a clear message if it fails.
        body.position_id = normalisedPid;
      } else if (canEditPositionId && normalisedPid && normalisedPid !== originalPid) {
        // Phase 2 (Jun 2026) — admins can change Position ID on edit. Only
        // include in the patch payload when it actually changed, otherwise
        // the backend would re-run the uniqueness check unnecessarily.
        body.position_id = normalisedPid;
      } else {
        delete body.position_id;
      }
      if (!body.assigned_hr_partner_id) delete body.assigned_hr_partner_id;
      if (isEdit) {
        await api.patch(`/team-members/${initial.id}`, body);
        toast.success("Team member updated");
      } else {
        await api.post("/team-members", body);
        toast.success(`Team member ${form.name} added`);
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="rounded-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit team member" : "Add team member"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="team-member-form">
          {(!isEdit || isEdit) && (
            <div className="space-y-1.5">
              <Label>Position ID</Label>
              <Input
                type="text"
                required={!isEdit}
                placeholder="e.g., 7GA00001"
                pattern="[A-Za-z0-9]{3,20}"
                title="3–20 letters or digits (uppercase enforced on save), e.g. 7GA00001"
                value={form.position_id}
                disabled={isEdit && !canEditPositionId}
                onChange={(e) =>
                  setForm((f) => ({
                    ...f,
                    position_id: e.target.value.toUpperCase(),
                  }))
                }
                data-testid="team-member-position-id-input"
              />
              {isEdit && canEditPositionId && (
                <p
                  className="text-[11px]"
                  style={{ color: "var(--page-fg-muted)" }}
                  data-testid="team-member-position-id-hint"
                >
                  Changing Position ID will be logged in the audit trail.
                </p>
              )}
              {isEdit && !canEditPositionId && (
                <p
                  className="text-[11px]"
                  style={{ color: "var(--page-fg-muted)" }}
                  data-testid="team-member-position-id-readonly-hint"
                >
                  Only admins can change Position ID.
                </p>
              )}
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="team-member-name-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Department</Label>
            <Input
              required
              value={form.department}
              onChange={(e) => setForm((f) => ({ ...f, department: e.target.value }))}
              data-testid="team-member-department-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label>Service line</Label>
            <Select
              value={form.service_line}
              onValueChange={(v) => setForm((f) => ({ ...f, service_line: v }))}
            >
              <SelectTrigger data-testid="team-member-service-line-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_LINES.map((s) => (
                  <SelectItem key={s} value={s}>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>
                      {nameOf(s)}{" "}
                      <span style={{ color: "var(--page-fg-muted)" }}>
                        ({s})
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Assigned HR partner</Label>
            <Select
              value={form.assigned_hr_partner_id || ""}
              onValueChange={(v) => setForm((f) => ({ ...f, assigned_hr_partner_id: v }))}
            >
              <SelectTrigger data-testid="team-member-hr-partner-select">
                <SelectValue placeholder="Select…" />
              </SelectTrigger>
              <SelectContent>
                {hrPartners.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="team-member-submit-button"
              className="bg-brand-teal text-white hover:bg-brand-tealDark"
            >
              {submitting ? (isEdit ? "Saving…" : "Adding…") : isEdit ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function TeamMembers() {
  const { user } = useAuth();
  const { nameOf } = useServiceLines();
  const [rows, setRows] = useState([]);
  const [hrPartners, setHrPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [serviceLineFilter, setServiceLineFilter] = useState("");
  const [partnerFilter, setPartnerFilter] = useState("");
  const [search, setSearch] = useState("");
  const [bulkOpen, setBulkOpen] = useState(false);
  // Phase 2 (Feb 2026 · v2) — directory stats banner + Export dropdown state.
  const [dirStats, setDirStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const canEdit = user?.role === "hr";
  const isAdmin = user?.is_admin === true;

  const loadDirStats = async () => {
    if (!isAdmin) return;
    try {
      const { data } = await api.get("/admin/team-members/directory-stats");
      setDirStats(data);
    } catch {
      // Silent — stats banner is a nice-to-have; page still works.
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const resp = await api.get("/admin/team-members/export", {
        params: { format, include_inactive: true },
        responseType: "blob",
      });
      // Trigger browser download via anchor + object URL.
      const disposition = resp.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/.exec(disposition);
      const stamp = new Date().toISOString().slice(0, 10);
      const fname = match
        ? match[1]
        : `goodwin-resolve-team-members-${stamp}.${format}`;
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      const rowCount = resp.headers?.["x-row-count"] || "";
      toast.success(
        rowCount
          ? `Exported ${rowCount} team member${rowCount === "1" ? "" : "s"} as ${format.toUpperCase()}`
          : `Team member directory exported as ${format.toUpperCase()}`,
      );
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setExporting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (showInactive) params.include_inactive = true;
      if (serviceLineFilter) params.service_line = serviceLineFilter;
      if (partnerFilter) params.hr_partner_id = partnerFilter;
      const [tmRes, partnersRes] = await Promise.all([
        api.get("/team-members", { params }),
        api.get("/config/hr-partners").catch(() => ({ data: [] })),
      ]);
      setRows(tmRes.data);
      setHrPartners(partnersRes.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadDirStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive, serviceLineFilter, partnerFilter]);

  const partnerMap = useMemo(
    () => Object.fromEntries(hrPartners.map((p) => [p.id, p.full_name])),
    [hrPartners]
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const needle = search.trim().toLowerCase();
    return rows.filter((t) =>
      [t.name, t.department, t.position_id, t.file_number != null ? String(t.file_number) : null]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(needle))
    );
  }, [rows, search]);

  const handleDeactivate = async (t) => {
    try {
      await api.post(`/team-members/${t.id}/deactivate`);
      toast.success(`${t.name} deactivated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  const handleActivate = async (t) => {
    try {
      await api.post(`/team-members/${t.id}/activate`);
      toast.success(`${t.name} reactivated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="team-members-page" className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--page-fg)]">Team members</h1>
          <p className="mt-1 text-sm text-[color:var(--page-fg-muted)]">
            {user?.role === "manager"
              ? "Your directly assigned team."
              : "Directory of team members across service lines."}
            {isAdmin && dirStats && (
              <>
                {" · "}
                <span data-testid="tm-directory-stats" className="font-medium text-[color:var(--page-fg-secondary)]">
                  Total: {dirStats.active} active
                  {dirStats.inactive > 0 && `, ${dirStats.inactive} inactive`}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    disabled={exporting}
                    data-testid="tm-export-button"
                  >
                    <Download className="mr-1 h-4 w-4" />
                    {exporting ? "Exporting…" : "Export"}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onSelect={() => handleExport("csv")}
                    data-testid="tm-export-csv"
                  >
                    Export as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleExport("xlsx")}
                    data-testid="tm-export-xlsx"
                  >
                    Export as XLSX (Excel)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                variant="outline"
                onClick={() => setBulkOpen(true)}
                data-testid="bulk-import-team-members-button"
              >
                <Upload className="mr-1 h-4 w-4" />
                Bulk import
              </Button>
              <BulkImportTeamMembersDialog
                open={bulkOpen}
                onOpenChange={setBulkOpen}
                onImportComplete={() => {
                  load();
                  loadDirStats();
                }}
              />
            </>
          )}
          {canEdit && (
            <TeamMemberDialog
              hrPartners={hrPartners}
              onSaved={load}
              trigger={
                <Button
                  data-testid="add-team-member-button"
                  className="bg-brand-teal text-white hover:bg-brand-tealDark"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add team member
                </Button>
              }
            />
          )}
        </div>
      </div>

      <div className="brand-card mb-4 flex flex-wrap items-center gap-3 p-3">
        <Input
          placeholder="Search by name, department, or position ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 w-72 rounded-xl border-[color:var(--border-subtle)] bg-[color:var(--surface-2)]"
          data-testid="team-member-search"
        />
        <Select
          value={serviceLineFilter || "all"}
          onValueChange={(v) => setServiceLineFilter(v === "all" ? "" : v)}
        >
          <SelectTrigger className="h-9 w-44" data-testid="filter-service-line">
            <SelectValue placeholder="All service lines" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All service lines</SelectItem>
            {SERVICE_LINES.map((s) => (
              <SelectItem key={s} value={s}>
                {nameOf(s)}{" "}
                <span style={{ color: "var(--page-fg-muted)" }}>({s})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={partnerFilter || "all"}
          onValueChange={(v) => setPartnerFilter(v === "all" ? "" : v)}
        >
          <SelectTrigger className="h-9 w-52" data-testid="filter-hr-partner">
            <SelectValue placeholder="All HR partners" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All HR partners</SelectItem>
            {hrPartners.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.full_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {canEdit && (
          <div className="ml-auto flex items-center gap-2">
            <Switch
              checked={showInactive}
              onCheckedChange={setShowInactive}
              data-testid="show-inactive-switch"
            />
            <Label className="text-xs text-[color:var(--page-fg-secondary)]">Show inactive</Label>
          </div>
        )}
      </div>

      <div className="brand-card overflow-hidden">
        <Table data-testid="team-members-table">
          <TableHeader>
            <TableRow className="brand-table-header border-b-brand-ivoryDark hover:bg-transparent">
              <TableHead>Position ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Department</TableHead>
              <TableHead>Service Line</TableHead>
              <TableHead>HR Partner</TableHead>
              <TableHead>Status</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-sm text-[color:var(--page-fg-muted)]">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && filteredRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="py-16 text-center text-sm text-[color:var(--page-fg-muted)]">
                  No team members match these filters.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filteredRows.map((t) => {
                const initials = (t.name || "??")
                  .split(" ")
                  .slice(0, 2)
                  .map((s) => s[0])
                  .join("")
                  .toUpperCase();
                return (
                  <TableRow
                    key={t.id}
                    data-testid={`team-member-row-${t.position_id || t.file_number}`}
                    className={`hover:bg-[color:var(--surface-2)] ${t.active === false ? "opacity-60" : ""}`}
                  >
                    <TableCell className="font-mono text-sm">{t.position_id || t.file_number}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[color:var(--surface-2)] text-xs font-semibold text-brand-teal">
                          {initials}
                        </span>
                        <span className="font-medium text-[color:var(--page-fg)]">{t.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{t.department}</TableCell>
                    <TableCell>
                      <span
                        className="inline-flex items-center rounded-full bg-[color:var(--surface-2)] px-2.5 py-0.5 text-xs font-medium text-brand-teal"
                        title={t.service_line}
                      >
                        {nameOf(t.service_line)}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-[color:var(--page-fg-secondary)]">
                      {partnerMap[t.assigned_hr_partner_id] || (
                        <span className="text-[color:var(--page-fg-muted)]">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {t.active === false ? (
                        <span className="inline-flex items-center rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700">
                          Inactive
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                          Active
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {canEdit && (
                        <div className="flex justify-end gap-1">
                          <TeamMemberDialog
                            initial={t}
                            hrPartners={hrPartners}
                            onSaved={load}
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`edit-team-member-${t.position_id || t.file_number}`}
                                className="text-[color:var(--page-fg-muted)] hover:text-brand-teal"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            }
                          />
                          {t.active === false ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleActivate(t)}
                              data-testid={`activate-team-member-${t.position_id || t.file_number}`}
                              className="text-[color:var(--page-fg-muted)] hover:text-emerald-700"
                            >
                              <Power className="h-3.5 w-3.5" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeactivate(t)}
                              data-testid={`deactivate-team-member-${t.position_id || t.file_number}`}
                              className="text-[color:var(--page-fg-muted)] hover:text-red-600"
                            >
                              <PowerOff className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
